<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  widthClass: { type: String, default: 'w-[320px]' },
});

const emit = defineEmits(['close']);

const triggerRef = ref(null);
const panelRef = ref(null);
const panelStyle = ref({});

const PANEL_GAP = 8;
const VIEW_PAD = 8;

const computePosition = () => {
  const trigger = triggerRef.value;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  // Gatilho que rolou pra fora da tela (a barra de chips tambem mora dentro da
  // bolha da sugestao, que rola junto com o thread): sem isto o painel ficaria
  // preso na borda, flutuando sozinho longe do chip que o abriu. Retangulo
  // zerado e ambiente sem layout (jsdom), e ali nada sai da tela.
  const measured = rect.width > 0 || rect.height > 0;
  if (measured && (rect.bottom <= 0 || rect.top >= window.innerHeight)) {
    emit('close');
    return;
  }
  const width = Math.min(320, window.innerWidth - VIEW_PAD * 2);
  let left = rect.left;
  if (left + width > window.innerWidth - VIEW_PAD) {
    left = window.innerWidth - width - VIEW_PAD;
  }
  if (left < VIEW_PAD) left = VIEW_PAD;

  const approxHeight = panelRef.value?.offsetHeight || 360;
  let top = rect.bottom + PANEL_GAP;
  if (
    top + approxHeight > window.innerHeight - VIEW_PAD &&
    rect.top > approxHeight + PANEL_GAP
  ) {
    top = rect.top - PANEL_GAP - approxHeight;
  }
  panelStyle.value = {
    top: `${Math.max(VIEW_PAD, top)}px`,
    left: `${left}px`,
    width: `${width}px`,
  };
};

const onDocPointer = event => {
  if (!props.open) return;
  const inTrigger = triggerRef.value?.contains(event.target);
  const inPanel = panelRef.value?.contains(event.target);
  if (!inTrigger && !inPanel) emit('close');
};

const onDocKey = event => {
  if (event.key === 'Escape' && props.open) {
    event.stopPropagation();
    emit('close');
  }
};

const onReposition = () => {
  if (props.open) computePosition();
};

watch(
  () => props.open,
  async open => {
    if (!open) return;
    await nextTick();
    computePosition();
    await nextTick();
    computePosition();
  }
);

if (typeof window !== 'undefined') {
  document.addEventListener('mousedown', onDocPointer);
  document.addEventListener('keydown', onDocKey);
  window.addEventListener('scroll', onReposition, true);
  window.addEventListener('resize', onReposition);
}

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocPointer);
  document.removeEventListener('keydown', onDocKey);
  window.removeEventListener('scroll', onReposition, true);
  window.removeEventListener('resize', onReposition);
});

defineExpose({ computePosition, triggerRef });
</script>

<template>
  <div ref="triggerRef" class="inline-flex min-w-0 max-w-full">
    <slot name="trigger" />
    <Teleport to="body">
      <div
        v-if="open"
        ref="panelRef"
        role="dialog"
        class="fixed z-[9999] max-h-[min(70vh,480px)] overflow-y-auto rounded-2xl border border-n-weak bg-n-solid-1 p-4 shadow-xl"
        :class="widthClass"
        :style="panelStyle"
        @click.stop
      >
        <div v-if="$slots.actions" class="float-right mb-2 ml-3">
          <slot name="actions" />
        </div>
        <slot />
      </div>
    </Teleport>
  </div>
</template>
